/**
 * Initialize the configuration for a simple WebGIS
 * based on a simplified template config
 * 
 * options = {
 *   template: "DefaultViewer",
 *   title: string,
 *   info: {                 // optional, can also be a HTML-String
 *     title: string,
 *     description: string,
 *     keywords: [],
 *     references: string
 *   },
 *   authors: [              // optional
 *     {
 *       name: string,
 *       organisation: string
 *       position: string,
 *       address: string,
 *       postCode: string,
 *       city: string,
 *       stateOrProvince: string,
 *       country: string
 *       phone: string,
 *       fax: string,
 *       email: string,
 *       orcid: string,
 *       uId: string,
 *       role: author
 *     }
 *   ],
 *   map: {}                 // override map defaults
 *   baseLayers: [],         // BaseLayer configs
 *   layers: [],             // Layer configs
 *   structure: [],          // LayerTree structure
 *   filters: [],            // filter configs,
 *   indexUrl: string
 *   allowAddingFilters: false
 * }
 * 
 * @memberof vef.viewer.templates.DefaultViewer
 * 
 * @param {object} options
 */
export function getViewerConfig(options) {

    options.title = options.title || "Map Content";
    options.info = options.info || null;
    options.authors = options.authors || null;
    options.map = options.map || {};
    options.baseLayers = options.baseLayers || {}
    options.layers = options.layers || {}
    options.structure = options.structure || [];
    options.filters = options.filters || [];
    options.customFunctions = options.customFunctions || [];
    options.templateFunctions = options.templateFunctions || [];
    options.cache = options.cache || {};
    options.tools = options.tools || {};

    // create info-element
    const info = (() => {
        switch (typeof options.info) {
            case "string":
                return {
                    type: "Template",
                    id: "info-element",
                    content: options.info
                };
            case "object":
                return Object.assign({
                    type: "ProjectInfo",
                    id: "info-element",
                }, options.info)
        }
        return null;
    })();

    const tools = [
        {
            type: "Zoom",
            position: "top-left"
        },
        {
            type: "ProjectionSwitcher",
            position: "top-left"
        },
        {
            type: "FitToScreen",
            position: "top-left"
        },
        {
            type: "DrawTools",
            position: "top-left"
        },
        {
            type: "LayerLegends",
            position: "top-left",
            targetTree: "layer-tree"
        },
        {
            type: "Screenshot",
            position: "top-left"
        },
        {
            type: "BaselayerSwitcher",
            position: "bottom-left",
            infoSidebar: "info-sidebar",
            infoSidebarGroup: "sidebar-info-content"
        },
        {
            type: "ScaleControl",
            position: "bottom-left"
        },
        {
            type: "CoordinateControl",
            position: "bottom-left"
        },
        {
            type: "Attribution",
            position: "bottom-right"
        }
    ].reduce((accumulator, tool) => {
        if (tool.type in options.tools) {
            if (typeof options.tools[tool.type] == "object") {
                accumulator.push(Object.assign({}, tool, options.tools[tool.type]));
            }
        } else {
            accumulator.push(tool);
        }
        return accumulator;
    }, []);

    // Create General Config
    return {
        theme: {},
        elements: [
            // Optional Info Element
            ... ((info) ? [info] : []),
            // Optional Authors Element
            ... ((options.authors) ? [{
                type: "Authors",
                id: "authors",
                authors: options.authors
            }] : []),
            // Default Elements
            {
                type: "Sidebar",
                id: "sidebar",
                position: "left",
                menu: true,
                closeable: true,
                responsive: true,
                groups: [
                    {
                        id: "sidebar-map",
                        title: options.title,
                        classes: "fas fa-map",
                        active: true
                    },
                    ... (info) ? [{
                        id: "sidebar-info-group",
                        title: "Information",
                        classes: "fas fa-info",
                        active: false
                    }] : [],
                    ... (options.authors) ? [{
                        id: "sidebar-authors-group",
                        title: "Authors & Contacts",
                        classes: "fas fa-users",
                        active: false
                    }] : []
                ]
            },
            {
                type: "Sidebar",
                id: "info-sidebar",
                position: "right",
                underlineHeader: true,
                menu: false,
                closeable: true,
                responsive: true,
                groups: [
                    {
                        id: "sidebar-popup-content"
                    },
                    {
                        id: "sidebar-info-content"
                    }
                ]
            },
            {
                type: "Map",
                id: "map",
                mode: "2D",
                popup: {
                    targetSidebar: "info-sidebar",
                    sidebarGroup: "sidebar-popup-content"
                },
                activeAreaSidebar: "sidebar",
                options: options.map,
                tools: tools
            },
            {
                type: "LayerTree",
                id: "layer-tree",
                title: "Layer Tree",
                targetMap: "map",
                targetSidebar: "info-sidebar",
                infoSidebarGroup: "sidebar-info-content",
                addLayerMethods: [
                    "ows",
                    "dws",
                    "catalog",
                    "importer"
                ],
                indexUrl: options.indexUrl,
                deleteEnabled: true,
                editEnabled: true,
                addFolderEnabled: true,
                structure: options.structure
            },
            {
                type: "FileDropArea",
                id: "drag-drop-area",
                dropTarget: "map",
                targetTree: "layer-tree"
            },
            ... (options.allowAddingFilters) ? [{
                type: "AddFilterButton",
                id: "add-filter-button",
                parentId: "sidebar-map"
            }] : []
        ],
        layout: [
            {
                parent: "#",
                children: [
                    "map",
                    "sidebar",
                    "info-sidebar"
                ]
            },
            {
                parent: "sidebar-map",
                children: ["layer-tree"].concat(options.filters.map(f => f.id)).concat((options.allowAddingFilters) ? ["add-filter-button"] : [])
            },
            ... (info) ? [{
                parent: "sidebar-info-group",
                children: ["info-element"]
            }] : [],
            ... (options.authors) ? [{
                parent: "sidebar-authors-group",
                children: ["authors"]
            }] : []
        ],
        layers: options.layers,
        cache: options.cache,
        baseLayers: options.baseLayers,
        filters: options.filters,
        customFunctions: options.customFunctions,
        templateFunctions: options.templateFunctions
    };
}

/**
 * convert a regular viewer config to the
 * PortalViewer config
 * @namespace vef.viewer.templates.DefaultViewer
 */
export function getTemplateConfig(config) {
    const result = {
        template: "DefaultViewer",
        title: null,
        info: null,
        authors: null,
        map: {},
        baseLayers: config.baseLayers,
        layers: config.layers,
        cache: config.cache,
        structure: [],
        filters: config.filters,
        customFunctions: config.customFunctions || [],
        templateFunctions: config.templateFunctions || []
    }

    // find specific element configs
    for (let i = 0; i < config.elements.length; ++i) {
        const element = config.elements[i];
        switch (element.id) {
            case "map":
                result.map = element.options;
                break;
            case "layer-tree":
                result.structure = element.structure;
                if ("indexUrl" in element) result.indexUrl = element.indexUrl;
                break;
            case "sidebar-map":
                result.title = element.dataset.title;
                break;
            case "info-element":
                result.info = (element.type == "Template") ? element.content : element;
                if ("id" in element) delete element.id;
                if ("type" in element) delete element.type;
                break;
            case "authors":
                result.authors = element.authors;
                break;
        }
    }

    return result;
}